import { useTheme } from '@/shared/providers/theme';
import { HolidayFilterParams } from '@/types/admin-holiday';
import { normalizeHolidayFilters } from '@/utils/holidaySearchParams';
import { convertWarekiDetail } from '@/utils/wareki';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppAdminFormLabel, AppButton, SearchTextField } from '../atoms';
import { Download, Search } from '../atoms/AppIcon';
import { RA2NDatePicker } from '../organisms';

interface AdminHolidayFilterProps {
  onSearch?: (filters: HolidayFilterParams) => void;
  onCsv?: () => void;
  csvLoading?: boolean;
}

/** Convert ISO date (YYYY-MM-DD) to 和暦 format for DatePicker display */
const isoToWareki = (iso: string): string => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const era = convertWarekiDetail(y, m, d);
  if (!era) return iso;
  return `${era}年${String(m).padStart(2, '0')}月${String(d).padStart(2, '0')}日`;
};

const getCurrentYearDefaults = () => {
  const year = new Date().getFullYear();
  return {
    start_date: isoToWareki(`${year}-01-01`),
    end_date: isoToWareki(`${year}-12-31`),
  };
};

/** Get wareki value from URL param: if ISO format, convert to wareki; otherwise use as-is */
const getDateParam = (value: string | null, fallback: string): string => {
  if (!value) return fallback;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return isoToWareki(value);
  return value;
};

const AdminHolidayFilter = ({ onSearch, onCsv, csvLoading }: AdminHolidayFilterProps) => {
  const [searchParams] = useSearchParams();
  const { colors } = useTheme();
  const defaults = getCurrentYearDefaults();

  const [filters, setFilters] = useState<HolidayFilterParams>({
    holiday_name: searchParams.get('holiday_name') || '',
    start_date: getDateParam(searchParams.get('start_date'), defaults.start_date),
    end_date: getDateParam(searchParams.get('end_date'), defaults.end_date),
  });

  useEffect(() => {
    setFilters({
      holiday_name: searchParams.get('holiday_name') || '',
      start_date: getDateParam(searchParams.get('start_date'), defaults.start_date),
      end_date: getDateParam(searchParams.get('end_date'), defaults.end_date),
    });
  }, [searchParams]);

  const handleSearchClick = () => {
    const normalizedFilters = normalizeHolidayFilters(filters);
    setFilters(normalizedFilters);
    onSearch?.(normalizedFilters);
  };

  const handleFilterChange = (key: keyof HolidayFilterParams, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = e.currentTarget;
      handleSearchClick();
      requestAnimationFrame(() => target.focus());
    }
  };

  return (
    <div className="bg-white rounded-md border p-4 mb-4">
      <div className="grid grid-cols-4 gap-4 items-end">
        <div>
          <AppAdminFormLabel label="休日名" />
          <SearchTextField
            placeholder="休日名"
            value={filters.holiday_name}
            onChange={e => handleFilterChange('holiday_name', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div>
          <AppAdminFormLabel label="開始日" />
          <RA2NDatePicker
            placeholder="開始日"
            value={filters.start_date}
            onChange={val => handleFilterChange('start_date', val)}
            onEnter={handleSearchClick}
          />
        </div>
        <div>
          <AppAdminFormLabel label="終了日" />
          <RA2NDatePicker
            placeholder="終了日"
            value={filters.end_date}
            onChange={val => handleFilterChange('end_date', val)}
            onEnter={handleSearchClick}
          />
        </div>
        <div className="flex gap-2">
          <AppButton
            onClick={handleSearchClick}
            icon={<Search />}
            type="primary"
            style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
          >
            検索
          </AppButton>
          <AppButton
            onClick={onCsv}
            icon={<Download />}
            type="primary"
            loading={csvLoading}
          >
            CSV
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default AdminHolidayFilter;
